import { useState } from "react";
import InstagramIcon from "../../../assets/svg/instagram-icon";
import StarIcon from "../../../assets/svg/star-icon";
import ChevronLeftIcon from "../../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../../assets/svg/chevron-right-icon";
import { useScrollNavigation } from "../../../hooks/use-scroll-navigation";
import { ModalityCardData } from "../utils/types";
import { GetMoreRequestModal } from "./GetMoreRequestModal";

export const ModalitiesSection = ({
  title = "Get More Modalities",
  description = "You’re currently using only two modalities, Expand to more channels to boost product utilization and reach a wider audience across platforms.",
  modalitiesData = [],
  isRequestModalOpen,
  onRequestModalOpenChange,
  submitApiType = "modalities",
}: {
  title?: string;
  description?: string;
  modalitiesData?: ModalityCardData[];
  isRequestModalOpen?: boolean;
  onRequestModalOpenChange?: (open: boolean) => void;
  submitApiType?: "modalities" | "features";
}) => {
  const [isGetMoreModalitiesModalOpen, setIsGetMoreModalitiesModalOpen] =
    useState(false);
  const {
    containerRef,
    isScrollable,
    canScrollLeft,
    canScrollRight,
    scrollTo,
    checkScrollState,
  } = useScrollNavigation(800);
  const hasModalities = modalitiesData.length > 0;
  const modalitiesRequestItems = modalitiesData.map((item) => ({
    code: item.code,
    icon: item.icon,
    iconColor: item.iconColor,
    title: item.title,
    description: item.description,
    badgeText: item.badge?.text,
    isLive: item.isLive,
  }));

  const handleOpenGetMoreModalitiesModal = () => {
    if (onRequestModalOpenChange) {
      onRequestModalOpenChange(true);
      return;
    }
    setIsGetMoreModalitiesModalOpen(true);
  };

  const handleGetMoreModalitiesModalOpenChange = (open: boolean) => {
    if (onRequestModalOpenChange) {
      onRequestModalOpenChange(open);
      return;
    }
    setIsGetMoreModalitiesModalOpen(open);
  };

  const isModalOpen = isRequestModalOpen ?? isGetMoreModalitiesModalOpen;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 pr-4">
        <div className="flex flex-col gap-1">
          <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
            {title}
          </span>
          <span className="text-xs text-data dark:text-data-dk">
            {description}
          </span>
        </div>
        {isScrollable && hasModalities && (
          <div className="flex gap-1">
            <button
              onClick={() => scrollTo("left")}
              disabled={!canScrollLeft}
              className="w-5 h-5 flex items-center justify-center rounded-[3px] text-verba-indigo dark:text-verba-indigo-dk bg-hover-effect dark:bg-hover-effect-dk disabled:bg-pure-white dark:disabled:bg-pure-white-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk disabled:hover:text-verba-indigo dark:disabled:hover:text-verba-indigo-dk disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeftIcon className="w-4 h-4 text-heading dark:text-heading-dk" />
            </button>
            <button
              onClick={() => scrollTo("right")}
              disabled={!canScrollRight}
              className="w-5 h-5 flex items-center justify-center rounded-[3px] text-verba-indigo dark:text-verba-indigo-dk bg-hover-effect dark:bg-hover-effect-dk disabled:bg-pure-white dark:disabled:bg-pure-white-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk disabled:hover:text-verba-indigo dark:disabled:hover:text-verba-indigo-dk disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRightIcon className="w-4 h-4 text-heading dark:text-heading-dk" />
            </button>
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        onScroll={checkScrollState}
        className="overflow-x-auto no-scrollbar pr-4"
      >
        {hasModalities ? (
          <div className="flex gap-3 w-max">
            {modalitiesData?.map((item) => {
              const Icon = item.icon;
              const ctaText = item.ctaText || `Get ${item?.title}`;
              return (
                <div
                  key={item?.title}
                  className="group relative w-[250px] shrink-0 min-h-[200px] justify-center items-start flex flex-col gap-3.5 p-3 border border-border-gray dark:border-border-gray-dk rounded-lg hover:border-selected-option-highlight dark:hover:border-selected-option-highlight-dk hover:bg-selected-option-highlight dark:hover:bg-selected-option-highlight-dk transition-all duration-300"
                >
                  <Icon className={`${item?.iconColor} w-6 h-6`} />
                  <div className="flex flex-col gap-3.5">
                    <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
                      {item?.title}
                    </span>
                    <span className="text-xs font-normal text-data dark:text-data-dk">
                      {item?.description}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="hidden group-hover:inline-flex w-full px-3 py-1 rounded-md text-sm font-semibold leading-5 text-center items-center justify-center whitespace-normal break-words text-pure-white dark:text-pure-white-dk bg-verba-indigo dark:bg-verba-indigo-dk border border-border-gray dark:border-border-gray-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-all duration-300"
                    onClick={handleOpenGetMoreModalitiesModal}
                  >
                    {ctaText}
                  </button>
                  {item?.badge?.text && (
                    <span className="absolute top-3 right-3 flex items-center gap-1 py-1 px-1.5 rounded text-xs font-semibold text-verba-sky dark:text-verba-sky-dk border border-border-gray dark:border-border-gray-dk group-hover:bg-verba-blue dark:group-hover:bg-verba-blue-dk group-hover:text-fixed-white dark:group-hover:text-fixed-white-dk transition-all duration-300">
                      <StarIcon className="w-3.5 h-3.5 text-rating-yellow dark:text-rating-yellow-dk" />
                      <span>{item?.badge?.text}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w-full h-[200px] rounded-lg border border-border-gray dark:border-border-gray-dk flex items-center justify-center text-sm text-data dark:text-data-dk">
            -
          </div>
        )}
      </div>
      <GetMoreRequestModal
        open={isModalOpen}
        onOpenChange={handleGetMoreModalitiesModalOpenChange}
        title="Get More Modalities"
        description="Upgrade your experience with extra modality options that help you work faster, smarter, and more efficiently."
        items={modalitiesRequestItems}
        showViewMore={true}
        submitApiType={submitApiType}
      />
    </div>
  );
};
